import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { CustomFieldValueFragmentDoc } from '~/types';
import specificResourceFragment from '~/modules/resource-management/enhancers/specificResourceFragment';
import { useHasFeatureFlag } from '~/modules/common/hooks';

export const UPDATE_USER_CUSTOM_FIELDS_MUTATION = gql`
  mutation UpdateUserCustomFields($input: UpdateUserCustomFieldInput!) {
    updateUserCustomFields2(input: $input) {
      customFieldValues {
        ...CustomFieldValue
      }
    }
  }
  ${CustomFieldValueFragmentDoc}
`;

export const updateCache = ({ userUri, isPsaPraaResourceMatchingEnabled }) => (
  proxy,
  mutationResponse
) => {
  const {
    data: {
      updateUserCustomFields2: { customFieldValues }
    }
  } = mutationResponse;

  const cachedFragmentId = `ResourceDetails:${userUri}`;

  const cachedUser = proxy.readFragment({
    id: cachedFragmentId,
    fragment: specificResourceFragment,
    fragmentName: 'SpecificResourceFragment',
    variables: { isPsaPraaResourceMatchingEnabled }
  });

  proxy.writeFragment({
    id: cachedFragmentId,
    fragmentName: 'SpecificResourceFragment',
    fragment: specificResourceFragment,
    variables: { isPsaPraaResourceMatchingEnabled },
    data: {
      ...cachedUser,
      customFieldValues
    }
  });
};

export const useUpdateUserCustomFieldsMutation = () =>
  useMutation(UPDATE_USER_CUSTOM_FIELDS_MUTATION);

const useUpdateUserCustomFields = ({ mapValuesOnSubmit, onClose }) => {
  const [error, setError] = useState();
  const isPsaPraaResourceMatchingEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPraaResourceMatchingEnabled'
  });

  const clearError = useCallback(() => setError(null), [setError]);

  const [updateUserCustomFields] = useUpdateUserCustomFieldsMutation();

  const onSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const payload = mapValuesOnSubmit ? mapValuesOnSubmit(values) : values;

      try {
        await updateUserCustomFields({
          variables: {
            input: payload
          },
          update: updateCache({
            userUri: values.id,
            isPsaPraaResourceMatchingEnabled
          })
        });
      } catch (err) {
        setError(err);
      } finally {
        setSubmitting(false);
        onClose();
      }
    },
    [
      mapValuesOnSubmit,
      updateUserCustomFields,
      isPsaPraaResourceMatchingEnabled,
      onClose
    ]
  );

  return { onSubmit, error, clearError };
};

export default useUpdateUserCustomFields;
